import { useCallback, useEffect, useRef, useState } from 'react';
import { IDP_V2_SESSION_ID_NAME } from '../utils/idpV2Auth';
import { sendAnalyticsHeartbeat } from '../utils/analyticsHeartbeat';
import { logger } from '../utils/logger';
import { useAuthToken } from './useAuthToken';
import {
  ACTIVE_ANALYTICS_ENGAGEMENT_EVENT,
  BACKEND_REQUEST_SENT_EVENT,
  getLastActiveAnalyticsEngagementAt,
  getLastBackendRequestSentAt
} from '../utils/backendRequestActivity';

const HEARTBEAT_INTERVAL_MS = 3 * 60 * 1000;

export function useAnalyticsHeartbeat() {
  const { token } = useAuthToken();
  const [sessionId, setSessionId] = useState<string | null>(() => localStorage.getItem(IDP_V2_SESSION_ID_NAME));
  const timeoutIdRef = useRef<number | null>(null);
  const lastHeartbeatSentAtRef = useRef(0);

  const refreshSessionId = useCallback(() => {
    setSessionId(localStorage.getItem(IDP_V2_SESSION_ID_NAME));
  }, []);

  const clearHeartbeatTimer = useCallback(() => {
    if (timeoutIdRef.current !== null) {
      window.clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }
  }, []);

  const sendHeartbeatIfSilent = useCallback(async () => {
    const currentSessionId = localStorage.getItem(IDP_V2_SESSION_ID_NAME);
    if (!token || !currentSessionId || document.visibilityState === 'hidden') {
      return;
    }

    const now = Date.now();
    if (now - getLastBackendRequestSentAt() < HEARTBEAT_INTERVAL_MS) {
      return;
    }
    if (now - getLastActiveAnalyticsEngagementAt() < HEARTBEAT_INTERVAL_MS) {
      return;
    }

    try {
      await sendAnalyticsHeartbeat(currentSessionId);
      lastHeartbeatSentAtRef.current = Date.now();
    } catch (error) {
      logger.debug('[AnalyticsHeartbeat] Failed to send heartbeat', error);
    }
  }, [token]);

  const scheduleHeartbeat = useCallback(() => {
    clearHeartbeatTimer();

    if (!token || !localStorage.getItem(IDP_V2_SESSION_ID_NAME)) {
      return;
    }

    const now = Date.now();
    const backendSilenceMs = now - getLastBackendRequestSentAt();
    const lastActiveAnalyticsEngagementAt = getLastActiveAnalyticsEngagementAt();
    const activeAnalyticsSilenceMs = lastActiveAnalyticsEngagementAt ? now - lastActiveAnalyticsEngagementAt : HEARTBEAT_INTERVAL_MS;
    const heartbeatSilenceMs = lastHeartbeatSentAtRef.current ? now - lastHeartbeatSentAtRef.current : HEARTBEAT_INTERVAL_MS;
    const delayMs = Math.max(
      0,
      HEARTBEAT_INTERVAL_MS - backendSilenceMs,
      HEARTBEAT_INTERVAL_MS - activeAnalyticsSilenceMs,
      HEARTBEAT_INTERVAL_MS - heartbeatSilenceMs
    );
    timeoutIdRef.current = window.setTimeout(async () => {
      await sendHeartbeatIfSilent();
      scheduleHeartbeat();
    }, delayMs);
  }, [clearHeartbeatTimer, sendHeartbeatIfSilent, token]);

  useEffect(() => {
    document.addEventListener('storageChange', refreshSessionId, false);
    window.addEventListener('storage', refreshSessionId, false);

    return () => {
      document.removeEventListener('storageChange', refreshSessionId);
      window.removeEventListener('storage', refreshSessionId);
    };
  }, [refreshSessionId]);

  useEffect(() => {
    if (!token || !sessionId) {
      clearHeartbeatTimer();
      return;
    }

    const onBackendRequestSent = () => {
      scheduleHeartbeat();
    };

    const onActiveAnalyticsEngagement = () => {
      scheduleHeartbeat();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void sendHeartbeatIfSilent().finally(scheduleHeartbeat);
      } else {
        clearHeartbeatTimer();
      }
    };

    scheduleHeartbeat();
    document.addEventListener(BACKEND_REQUEST_SENT_EVENT, onBackendRequestSent, false);
    document.addEventListener(ACTIVE_ANALYTICS_ENGAGEMENT_EVENT, onActiveAnalyticsEngagement, false);
    document.addEventListener('visibilitychange', onVisibilityChange, false);

    return () => {
      clearHeartbeatTimer();
      document.removeEventListener(BACKEND_REQUEST_SENT_EVENT, onBackendRequestSent);
      document.removeEventListener(ACTIVE_ANALYTICS_ENGAGEMENT_EVENT, onActiveAnalyticsEngagement);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [clearHeartbeatTimer, scheduleHeartbeat, sendHeartbeatIfSilent, sessionId, token]);
}
